/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View } from '@components/atoms'
import { Seo, Footer, KoinosWordmark, KedemWordmark } from '@components/systems'
import { mono, monoShared } from '@components/systems/monolith/monolith.tokens'
import { manualList, ManualLanguageType } from '@src/configs/manual.config'
import { Analytics } from '@utils'

import kedem6Img from '@public/images/pages/shop/kedem6/thumbnail/kedem6-thumb2.jpg'
import kedem4Img from '@public/images/pages/shop/kedem4/thumbnail/kedem4-thumb2.jpg'
import pls8Img from '@public/images/monolith/koinos8-top-angle-screen-m54.png'

/** 코드 → 썸네일 (Cart 와 같은 컷). 제품이 늘면 여기에 한 줄. */
const imageObj: { [code: string]: string } = {
	kdm6: kedem6Img,
	kdm4: kedem4Img,
	pls8: pls8Img,
}

/**
 * PDF 를 저장한다. S3 가 cross-origin 이라 `<a download>` 는 브라우저가 무시하므로
 * blob 으로 받아 내려준다(버킷 CORS 필요). CORS 가 막혀 있으면 새 탭으로 연다 — Open 과 같아진다.
 */
const downloadPdf = async (aLang: ManualLanguageType) => {
	try {
		const res = await fetch(aLang.url)
		if (!res.ok) throw new Error(String(res.status))
		const blob = await res.blob()
		const objectUrl = URL.createObjectURL(blob)
		const aLink = document.createElement('a')
		aLink.href = objectUrl
		aLink.download = aLang.fileName
		document.body.appendChild(aLink)
		aLink.click()
		document.body.removeChild(aLink)
		setTimeout(() => URL.revokeObjectURL(objectUrl), 10_000)
	} catch {
		window.open(aLang.url, '_blank', 'noopener')
	}
}

/**
 * Manuals — Monolith(2안). `/manuals` 한 장으로 끝난다.
 *
 * 제품 하나 = 헤어라인 행 하나. 왼쪽 썸네일(Cart 와 같은 컷) + 이름, 오른쪽 언어별 행에
 * Open(브라우저 PDF 뷰어) · Download 두 동작. 하위 페이지·내장 뷰어·아코디언 없음.
 */
const MonolithManualsPage = () => {
	return (
		<View css={style}>
			<Seo />

			<View className="mnWrap">
				<View className="mnPageHeader">
					<h1 className="mnPageTitle">Manuals</h1>
					<p className="mnPageLede">
						User manuals for every product. PDF, in up to ten languages.
					</p>
				</View>

				<View className="mnList">
					{manualList.map(aManual => {
						const image = imageObj[aManual.code]
						const isPublished = aManual.status === 'published'
						return (
							<View key={aManual.code} className="mnRow">
								<View className="mnRowHead">
									{image !== undefined && (
										<View className="mnThumb">
											<img src={image} alt="" className="mnThumbImage" />
										</View>
									)}
									<h2 className="mnRowTitle">
										{aManual.code === 'pls8' ? (
											<KoinosWordmark as="span" />
										) : aManual.code.startsWith('kdm') ? (
											<KedemWordmark as="span" model={aManual.code.replace('kdm', '')} />
										) : (
											aManual.name
										)}
									</h2>
								</View>

								<View className="mnRowBody">
									{isPublished ? (
										<View className="mnLangs">
											{aManual.languages.map(aLang => (
												<View key={aLang.code} className="mnLang">
													<span className="mnLangLabel">
														{aLang.label}
														{aLang.native !== aLang.label && (
															<span className="mnLangNative">{aLang.native}</span>
														)}
													</span>
													<View className="mnLangActions">
														<a
															className="mnAction"
															href={aLang.url}
															target="_blank"
															rel="noopener noreferrer"
															onClick={() =>
																Analytics.download('download_manual', {
																	manual_code: aManual.code,
																	manual_language: aLang.code,
																	method: 'open',
																})
															}
														>
															Open
														</a>
														<button
															type="button"
															className="mnAction"
															onClick={() => {
																Analytics.download('download_manual', {
																	manual_code: aManual.code,
																	manual_language: aLang.code,
																	method: 'download',
																})
																void downloadPdf(aLang)
															}}
														>
															Download
														</button>
													</View>
												</View>
											))}
										</View>
									) : (
										<span className="mnRowMeta">Not yet published</span>
									)}
								</View>
							</View>
						)
					})}
				</View>
			</View>

			<Footer />
		</View>
	)
}

export default MonolithManualsPage

const style = css({
	...monoShared,
	position: 'relative',
	width: '100%',

	// 페이지 헤더 — 1열. 제목은 전폭, 소제목은 그 아래(DESIGN_SYSTEM §6.2). 4:8 은 두 단어 제목(Works with)이 줄바꿈돼 폐기.
	'.mnPageHeader': {
		padding: 'clamp(64px, 8.3vw, 120px) 0 clamp(48px, 6.25vw, 90px)',

		'.mnPageTitle': {
			fontSize: 'clamp(56px, 7.6vw, 110px)',
			lineHeight: 1,
			fontWeight: 100,
			letterSpacing: '-0.01em',
			color: mono.ink,
		},
		'.mnPageLede': {
			fontSize: 19,
			lineHeight: 1.6,
			fontWeight: 300,
			color: mono.dim,
			maxWidth: 520,
			marginTop: 'clamp(20px, 2vw, 28px)',
		},
	},

	'.mnList': {
		borderTop: `1px solid ${mono.line}`,
		marginBottom: 'clamp(64px, 8.3vw, 120px)',
	},

	// 제품 행 — 4:8. 왼쪽 썸네일+이름, 오른쪽 언어 리스트.
	'.mnRow': {
		display: 'grid',
		gridTemplateColumns: 'minmax(0, 4fr) minmax(0, 8fr)',
		gap: 48,
		padding: 'clamp(40px, 4.2vw, 60px) 0',
		borderBottom: `1px solid ${mono.line}`,

		'@media (max-width: 900px)': {
			gridTemplateColumns: '1fr',
			gap: 28,
		},
	},

	'.mnRowHead': {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'flex-start',
		gap: 24,

		'.mnThumb': {
			width: 220,
			maxWidth: '100%',
			aspectRatio: '3 / 2',
			border: `1px solid ${mono.line}`,
			borderRadius: 6,
			backgroundColor: mono.surface,
			overflow: 'hidden',
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center',

			'.mnThumbImage': {
				width: '100%',
				height: '100%',
				objectFit: 'cover',
				display: 'block',
			},
		},

		'.mnRowTitle': {
			fontSize: 'clamp(28px, 2.8vw, 40px)',
			lineHeight: 1.1,
			fontWeight: 300,
			color: mono.ink,
		},
	},

	'.mnRowBody': {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'flex-start',
		justifyContent: 'center',

		'.mnRowMeta': {
			fontSize: 13,
			letterSpacing: '.18em',
			textTransform: 'uppercase',
			color: mono.faint,
			fontWeight: 500,
		},
	},

	// 언어별 헤어라인 행 — 왼쪽 언어, 오른쪽 Open · Download
	'.mnLangs': {
		width: '100%',
		maxWidth: 620,
		borderTop: `1px solid ${mono.line}`,

		'.mnLang': {
			display: 'flex',
			justifyContent: 'space-between',
			alignItems: 'center',
			gap: 24,
			padding: '16px 0',
			borderBottom: `1px solid ${mono.line}`,

			'.mnLangLabel': {
				display: 'flex',
				alignItems: 'baseline',
				gap: 14,
				fontSize: 16,
				fontWeight: 400,
				color: mono.ink,
			},
			'.mnLangNative': {
				fontSize: 14,
				fontWeight: 300,
				color: mono.faint,
			},

			'.mnLangActions': {
				display: 'flex',
				alignItems: 'center',
				gap: 28,
				flexShrink: 0,
			},

			'.mnAction': {
				fontFamily: 'inherit',
				fontSize: 13,
				fontWeight: 500,
				letterSpacing: '.02em',
				color: mono.dim,
				background: 'transparent',
				border: 0,
				padding: 0,
				paddingBottom: 2,
				borderBottom: '1px solid transparent',
				cursor: 'pointer',
				transition: 'color 200ms ease-out, border-color 200ms ease-out',

				'&:hover': {
					color: mono.ink,
					borderBottomColor: mono.ink,
				},
				'&:focus-visible': {
					outline: `1px solid ${mono.ink}`,
					outlineOffset: 3,
				},
				'@media (prefers-reduced-motion: reduce)': {
					transition: 'none',
				},
			},
		},
	},
})
